import { Eye } from "lucide-react";
import { useState } from "react";

import type { JDDetail } from "@/api/jd-queries";
import { useMemberDirectory } from "@/api/queries";
import { NumberChips } from "@/components/jd-details-parts";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Empty, Panel, PanelHeader } from "@/components/ui/panel";
import { Switch } from "@/components/ui/switch";
import { savedDetails, useDraft, type Details } from "@/lib/jd-draft-state";
import { experience } from "@/lib/jd-steps";
import { cn, formatDate } from "@/lib/utils";

/**
 * Step 1, Details — DESIGN §20.8, §20.17 (audience). The position as the analysis read it, for the
 * Resource Manager to correct. Its fields join the page's unsaved changes like every other step, so one
 * Save keeps everything; the client's original text opens in a drawer instead of pushing the form down.
 */

const label = "mb-1 block text-[12.5px] font-medium";
const hint = "text-bank-muted mt-1 text-xs";

export function DetailsStep({
  jd,
  canManage,
  onShowText,
  onSeeQuestions,
}: {
  jd: JDDetail;
  canManage: boolean;
  onShowText: () => void;
  onSeeQuestions: () => void;
}) {
  const draft = useDraft();
  const directory = useMemberDirectory(true);
  // Only a directory Workspace actually answered; otherwise the fields stay plain number entry.
  const people = directory.data?.available ? directory.data.members : undefined;
  const saved = savedDetails(jd);
  const form = draft.details(saved);
  const set = (patch: Partial<Details>) => draft.patchDetails(patch, saved);
  const years = (v: string) =>
    v.trim() === "" ? null : Math.max(0, Math.min(50, Math.floor(Number(v)) || 0));
  const [chosen, setChosen] = useState(form.audience_members.length > 0 || form.audience_teams.length > 0);
  const restricted = chosen || form.audience_members.length > 0 || form.audience_teams.length > 0;

  return (
    <div className="flex flex-wrap items-start gap-3">
      <fieldset disabled={!canManage} className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
        <Panel>
          <PanelHeader
            title="The position"
            actions={
              <span className="text-bank-muted text-xs">
                As the AI read it — correct anything it got wrong
              </span>
            }
          />
          <div className="space-y-3 p-4">
            <div>
              <label htmlFor="jd-title" className={label}>
                Title
              </label>
              <Input id="jd-title" value={form.title} onChange={(e) => set({ title: e.target.value })} />
            </div>
            <div>
              <label htmlFor="jd-summary" className={label}>
                Summary
              </label>
              <Textarea
                id="jd-summary"
                value={form.summary}
                onChange={(e) => set({ summary: e.target.value })}
              />
            </div>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-[1_1_260px]">
                <span className={label}>Experience</span>
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Experience from, in years"
                    type="number"
                    min={0}
                    max={50}
                    className="w-20"
                    value={form.experience_min ?? ""}
                    onChange={(e) => set({ experience_min: years(e.target.value) })}
                  />
                  <span className="text-bank-muted text-sm">to</span>
                  <Input
                    aria-label="Experience up to, in years"
                    type="number"
                    min={0}
                    max={50}
                    className="w-20"
                    value={form.experience_max ?? ""}
                    onChange={(e) => set({ experience_max: years(e.target.value) })}
                  />
                  <span className="text-bank-muted text-sm">years</span>
                </div>
                <p className={hint}>
                  Leave "to" empty for "5+ years". Interview difficulty is pitched from this.
                </p>
              </div>
              <div className="flex-[1_1_220px]">
                <label htmlFor="jd-expires" className={label}>
                  Expires on
                </label>
                <Input
                  id="jd-expires"
                  type="date"
                  value={form.expires_at ?? ""}
                  onChange={(e) => set({ expires_at: e.target.value || null })}
                />
                <p className={hint}>After this day it leaves the start form on its own.</p>
              </div>
            </div>
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Client" />
          <div className="flex flex-wrap items-end gap-x-5 gap-y-3 p-4">
            <div className="flex-[1_1_240px]">
              <label htmlFor="jd-client" className={label}>
                Client name
              </label>
              <Input
                id="jd-client"
                value={form.client_name}
                onChange={(e) => set({ client_name: e.target.value })}
              />
            </div>
            <div className="flex flex-[1_1_260px] items-center gap-2.5 pb-1.5 text-[13px]">
              <Switch
                checked={form.show_client}
                onChange={(show_client) => set({ show_client })}
                label="Show the client's name to members"
                disabled={!canManage}
              />
              Show the client's name to members
            </div>
            <p className="bg-bank-surface-2 w-full rounded-md px-2.5 py-2 text-[12.5px]">
              <span className="text-bank-muted mr-1.5 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Members see
              </span>
              <strong>{form.title || "Untitled"}</strong>
              {form.show_client && form.client_name.trim() ? ` · ${form.client_name.trim()}` : ""}
              <span className="text-bank-muted">
                {" "}
                · {experience(form.experience_min, form.experience_max)}
              </span>
            </p>
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Who can practise it" />
          <div className="space-y-3 p-4">
            <div role="radiogroup" aria-label="Who can practise it" className="flex flex-wrap gap-2.5">
              {(
                [
                  [false, "Every member", "It shows on everyone's start form once published."],
                  [true, "Only people I choose", "Named members, and teams by their manager."],
                ] as const
              ).map(([value, title, text]) => (
                <label
                  key={title}
                  className={cn(
                    "flex flex-[1_1_220px] cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5",
                    restricted === value
                      ? "border-bank-accent bg-bank-accent-wash"
                      : "border-bank-border-strong",
                  )}
                >
                  <input
                    type="radio"
                    name="jd-audience"
                    className="accent-bank-accent mt-1"
                    checked={restricted === value}
                    onChange={() => {
                      setChosen(value);
                      if (!value) set({ audience_members: [], audience_teams: [] });
                    }}
                  />
                  <span>
                    <span className="block text-[13.5px] font-semibold">{title}</span>
                    <span className="text-bank-muted text-[12.5px]">{text}</span>
                  </span>
                </label>
              ))}
            </div>
            {restricted && (
              <>
                <div>
                  <label htmlFor="jd-members" className={label}>
                    Members <span className="text-bank-muted font-normal">— member numbers</span>
                  </label>
                  <NumberChips
                    id="jd-members"
                    values={form.audience_members}
                    onChange={(audience_members) => set({ audience_members })}
                    placeholder="Type or paste numbers, Enter to add"
                    disabled={!canManage}
                    directory={people}
                  />
                </div>
                <div>
                  <label htmlFor="jd-teams" className={label}>
                    Teams <span className="text-bank-muted font-normal">— the manager's member number</span>
                  </label>
                  <NumberChips
                    id="jd-teams"
                    values={form.audience_teams}
                    onChange={(audience_teams) => set({ audience_teams })}
                    placeholder="Manager's member number"
                    disabled={!canManage}
                    directory={people}
                  />
                  {/* §20.17: Workspace gives ipp-platform a member's own reporting officer and nothing above it. */}
                  <p className={hint}>
                    A team is the manager and the people who report to them directly. Add a sub-manager to
                    include their team too.
                  </p>
                </div>
                {form.audience_members.length === 0 && form.audience_teams.length === 0 && (
                  <p className="text-bank-attention-ink text-xs">
                    Nobody named yet — until someone is, every member can practise it.
                  </p>
                )}
              </>
            )}
          </div>
        </Panel>
      </fieldset>

      <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
        <Panel>
          <PanelHeader title="Where it came from" />
          <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 p-4 text-[13px]">
            <dt className="text-bank-muted">File</dt>
            <dd className="font-mono text-[11.5px] break-all">{jd.source.filename ?? "pasted text"}</dd>
            <dt className="text-bank-muted">Uploaded</dt>
            <dd>
              {formatDate(jd.source.created_at)}
              {jd.source.uploaded_by ? ` by ${jd.source.uploaded_by}` : ""}
            </dd>
            <dt className="text-bank-muted">Read by</dt>
            <dd className="font-mono text-[11.5px]">{jd.analysis_model ?? "—"}</dd>
            <dt className="text-bank-muted">In this upload</dt>
            <dd>
              {jd.source.positions} position{jd.source.positions === 1 ? "" : "s"}
            </dd>
          </dl>
          {canManage && (
            <div className="border-bank-border border-t px-4 py-2.5">
              <Button size="sm" onClick={onShowText}>
                <Eye className="size-4" aria-hidden="true" />
                View the client's original text
              </Button>
            </div>
          )}
        </Panel>
        <Panel>
          <PanelHeader title={`Questions written in the JD · ${jd.source_questions.length}`} />
          {jd.source_questions.length === 0 ? (
            <Empty>The JD carried no interview questions.</Empty>
          ) : (
            <ol className="list-decimal space-y-1.5 py-3 pr-4 pl-9 text-[13px]">
              {jd.source_questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ol>
          )}
          {jd.source_questions.length > 0 && (
            <p className="text-bank-muted border-bank-border border-t px-4 py-2 text-xs">
              Each became one of this JD's own questions —{" "}
              <button type="button" className="text-bank-accent-ink hover:underline" onClick={onSeeQuestions}>
                follow them on Questions
              </button>
              .
            </p>
          )}
        </Panel>
      </aside>
    </div>
  );
}
